import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { AdminNonConformanceRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { IdName } from 'src/app/shared/models/id-name';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { SeaStateApiService } from '../../services/sea-state/sea-state-api.service';
import {
  CreateSeaState,
  DeleteSeaState,
  LoadAllSeaStates,
  LoadSeaStateById,
  LoadSeaStates,
  UpdateSeaState,
  ClearCurrentSeaState,
} from './non-conformance-sea-state.actions';

export interface SeaStateStateModel {
  seaStates: IdName[];
  allSeaStates: IdName[];
  seaStateTotalPages: number;
  currentSeaState: IdName | undefined;
}

const NC_SEA_STATE_STATE_TOKEN = new StateToken<SeaStateStateModel>('ncSeaStates');

@State<SeaStateStateModel>({
  name: NC_SEA_STATE_STATE_TOKEN,
  defaults: {
    seaStates: [],
    allSeaStates: [],
    seaStateTotalPages: 0,
    currentSeaState: undefined,
  },
})
@Injectable()
export class NonConformanceSeaStateState {
  constructor(private service: SeaStateApiService) {}

  @Action(LoadSeaStates)
  loadSeaStates(
    { getState, setState }: StateContext<SeaStateStateModel>,
    { filter }: LoadSeaStates
  ) {
    return this.service.loadSeaStates(filter).pipe(
      tap((response: ApiResponse<IdName[]>) => {
        const state = getState();
        setState({
          ...state,
          seaStates: response.data,
          seaStateTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadAllSeaStates, { cancelUncompleted: true })
  loadAllSeaStates(ctx: StateContext<SeaStateStateModel>) {
    return this.service.loadAllSeaStates().pipe(
      tap((allSeaStates: IdName[]) => {
        ctx.setState(patch({ allSeaStates }));
      })
    );
  }

  @Action(LoadSeaStateById, { cancelUncompleted: true })
  loadSeaStateById(
    { getState, setState }: StateContext<SeaStateStateModel>,
    { id }: LoadSeaStateById
  ) {
    const { seaStates } = getState();
    const exist = seaStates.some((i) => i.id === id);
    return this.service.loadSeaStateById(id).pipe(
      tap((seaState: IdName) => {
        setState(
          patch({
            seaStates: exist
              ? updateItem<IdName>((toUpdate) => toUpdate?.id === id, seaState)
              : insertItem<IdName>(seaState),
            currentSeaState: seaState,
          })
        );
      })
    );
  }

  @Action(CreateSeaState)
  @OnSuccess({ url: AdminNonConformanceRoutes.SeaState, message: 'seaStateAdded' })
  createSeaState({ setState }: StateContext<SeaStateStateModel>, { seaState }: CreateSeaState) {
    return this.service.createSeaState(seaState).pipe(
      tap((response: IdName) => {
        setState(
          patch({
            seaStates: insertItem<IdName>(response),
          })
        );
      })
    );
  }

  @Action(UpdateSeaState)
  @OnSuccess({ url: AdminNonConformanceRoutes.SeaState, message: 'seaStateUpdated' })
  updateSeaState({ setState }: StateContext<SeaStateStateModel>, { seaState }: UpdateSeaState) {
    return this.service.updateSeaState(seaState).pipe(
      tap(() => {
        setState(
          patch({
            seaStates: updateItem<IdName>((toUpdate) => toUpdate?.id === seaState.id, seaState),
          })
        );
      })
    );
  }

  @Action(DeleteSeaState, { cancelUncompleted: true })
  @OnSuccess({
    url: AdminNonConformanceRoutes.SeaState,
    message: 'seaStateDeleted',
    dispatch: ResetCurrentFilter,
  })
  deleteSeaState(ctx: StateContext<SeaStateStateModel>, { id }: DeleteSeaState) {
    return this.service.deleteSeaState(id).pipe(
      tap(() => {
        ctx.setState(
          patch({
            seaStates: removeItem<IdName>(
              (seaStateToDelete: IdName | undefined) => seaStateToDelete?.id === id
            ),
          })
        );
      })
    );
  }

  @Action(ClearCurrentSeaState)
  clearCurrentSeaState(ctx: StateContext<SeaStateStateModel>) {
    ctx.setState(patch({ currentSeaState: undefined }));
  }

  @Selector()
  static getSeaStatesTotalPages(state: SeaStateStateModel) {
    return state.seaStateTotalPages;
  }

  @Selector()
  static seaStates(state: SeaStateStateModel) {
    return state.seaStates;
  }

  @Selector()
  static allSeaStates(state: SeaStateStateModel): IdName[] {
    return state.allSeaStates;
  }

  @Selector()
  static getSeaStateById(state: SeaStateStateModel) {
    return (id: string) => {
      return state.seaStates.find((seaState: IdName) => seaState.id === id);
    };
  }

  @Selector()
  static getCurrentSeaState(state: SeaStateStateModel) {
    return state.currentSeaState;
  }
}
